import React, { Fragment, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleReCaptchaProvider, GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { navigate } from 'gatsby-link';
import { Link } from 'gatsby';

import Input from '@components/Input';
import Alert from '@components/Alert';
import { Grid, Divider, Col, Row } from '@components/Grid';
import { makeGetRequest, makePostRequest } from '@helpers/requests';
import { USER_REGISTER, COUNTRIES } from '@helpers/api';
import { loginUser } from '@actions/user';
import { useSnackbar } from '@components/Snackbar';
import Card from '@components/Card';
import Select from '@components/Select';
import { useLocation } from '@reach/router';
import {
    getCustomFieldsInitialValues,
    getCustomFieldsSubmitValues,
    getCustomFieldsValidation,
} from '@helpers/customField';
import CustomFields from '@components/CustomFields';
import styles from './styles.module.scss';

const RegisterForm = ({ accountType }) => {
    const user = useSelector(({ user }) => user);
    const location = useLocation();
    const [openSnackbar] = useSnackbar();
    const dispatch = useDispatch();
    const [countries, setCountries] = useState([]);
    const [recaptchaToken, setRecaptchaToken] = useState(false);

    console.log(accountType)


    useEffect(() => {
        if (user && !accountType) {
            // user already exists, redirect to account overview
            navigate('/account/overview');
            openSnackbar("You're already logged in");
        }
    }, []);

    useEffect(() => {
        if (user && !accountType) {
            // user was created, take user to account overview
            navigate('/account/overview');
        }

        (async () => {
            try {
                const { data: countriesData } = await makeGetRequest(COUNTRIES);
                setCountries(countriesData);
            } catch (error) {
                error !== 'cancelled' &&
                openSnackbar(error?.errorMessage ?? 'An error occurred with registering.');
            }
        })();
    }, [user]);

    const handleSubmit = async (values, actions) => {
        try {
            // if (!recaptchaToken) {
            //     openSnackbar(
            //         'Please complete the recaptcha verification in order to register an account.'
            //     );
            //     return;
            // }

            const { data } = await makePostRequest(USER_REGISTER, {
                ...values,
                customFields: getCustomFieldsSubmitValues(
                    values?.customFields,
                    accountType?.customFields
                ),
                // recaptcha: recaptchaToken,
                type: accountType?.id,
            });

            dispatch(loginUser(data));
            openSnackbar(`Successfully created an account, welcome ${values.firstName}`);

            actions.setSubmitting(false);
            navigate('/account/overview');
        } catch (error) {
            console.log({ error });
            error !== 'cancelled' &&
            openSnackbar(
                error?.errorMessage ?? 'An error occurred when attempting to register.'
            );
            actions.setSubmitting(false);
        }
    };

    const handleRecaptchaVerify = token => {
        setRecaptchaToken(token);
    };

    const yupObjectSchema = Yup.object().shape({
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        email: Yup.string()
            .email('Must be an email address')
            .lowercase()
            .required('Email Address is required'),
        password: Yup.string().required('Password is required'),
        locale: Yup.object().shape({
            countryCode: Yup.string().required('Country is required'),
            city: Yup.string().nullable(),
        }),
        customFields: getCustomFieldsValidation(accountType?.customFields),
    });

    return (
            <Grid>
                <Formik
                    validationSchema={yupObjectSchema}
                    initialValues={{
                        firstName: '',
                        lastName: '',
                        email: '',
                        password: '',
                        locale: {
                            countryCode: '',
                            city: null,
                        },
                        customFields: getCustomFieldsInitialValues(accountType?.customFields),
                    }}
                    onSubmit={handleSubmit}
                >
                    {formikProps => {
                        const {
                            handleSubmit,
                            values,
                            errors,
                            touched,
                            isSubmitting,
                            handleChange,
                        } = formikProps;

                        return (
                            <Fragment>
                                <Card>
                                    <Card.Title>
                                        {!!accountType
                                            ? `Register as a ${accountType?.name ?? ''}`
                                            : 'Register'}
                                    </Card.Title>
                                    <Card.Content>
                                        <div className={styles.registerContent}>
                                            <Row>
                                                <Col xs={12} lg={9} xl={8}>
                                                    <Input
                                                        label="First Name"
                                                        name="firstName"
                                                        value={values.firstName}
                                                        onChange={handleChange}
                                                    />
                                                    {touched?.firstName && errors.firstName && (
                                                        <Fragment>
                                                            <Divider margin={2} />
                                                            <Alert
                                                                type="error"
                                                                message={errors.firstName}
                                                            />
                                                        </Fragment>
                                                    )}
                                                    <Divider />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12} lg={9} xl={8}>
                                                    <Input
                                                        label="Last Name"
                                                        name="lastName"
                                                        value={values.lastName}
                                                        onChange={handleChange}
                                                    />
                                                    {touched?.lastName && errors.lastName && (
                                                        <Fragment>
                                                            <Divider margin={2} />
                                                            <Alert
                                                                type="error"
                                                                message={errors.lastName}
                                                            />
                                                        </Fragment>
                                                    )}
                                                    <Divider />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12} lg={9} xl={8}>
                                                    <Input
                                                        label="Email Address"
                                                        name="email"
                                                        type="email"
                                                        value={values.email}
                                                        onChange={handleChange}
                                                    />
                                                    {touched?.email && errors.email && (
                                                        <Fragment>
                                                            <Divider margin={2} />
                                                            <Alert
                                                                type="error"
                                                                message={errors.email}
                                                            />
                                                        </Fragment>
                                                    )}
                                                    <Divider />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12} lg={9} xl={8}>
                                                    <Input
                                                        type="password"
                                                        label="Password"
                                                        name="password"
                                                        value={values.password}
                                                        onChange={handleChange}
                                                    />
                                                    {touched?.password && errors.password && (
                                                        <Fragment>
                                                            <Divider margin={2} />
                                                            <Alert
                                                                type="error"
                                                                message={errors.password}
                                                            />
                                                        </Fragment>
                                                    )}
                                                    <Divider />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12} lg={9} xl={8}>
                                                    <Select
                                                        label="Country"
                                                        name="locale.countryCode"
                                                        value={values.locale?.countryCode}
                                                        onChange={handleChange}
                                                        placeholder="Select a country..."
                                                    >
                                                        {countries
                                                            .sort((a, b) => {
                                                                if (a.name > b.name) {
                                                                    return 1;
                                                                }

                                                                if (b.name > a.name) {
                                                                    return -1;
                                                                }

                                                                return 0;
                                                            })
                                                            .map(({ name, code }) => (
                                                                <Select.Option
                                                                    key={code}
                                                                    label={name}
                                                                    value={code}
                                                                />
                                                            ))}
                                                    </Select>
                                                    {touched?.locale?.countryCode &&
                                                    errors.locale?.countryCode && (
                                                        <Fragment>
                                                            <Divider margin={2} />
                                                            <Alert
                                                                type="error"
                                                                message={
                                                                    errors.locale?.countryCode
                                                                }
                                                            />
                                                        </Fragment>
                                                    )}
                                                    <Divider />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12} lg={9} xl={8}>
                                                    <Input
                                                        type="city"
                                                        optional
                                                        label="City"
                                                        name="locale.city"
                                                        value={values.locale?.city || ''}
                                                        onChange={handleChange}
                                                    />
                                                    {touched?.locale?.city && errors.locale?.city && (
                                                        <Fragment>
                                                            <Divider margin={2} />
                                                            <Alert
                                                                type="error"
                                                                message={errors.locale?.city}
                                                            />
                                                        </Fragment>
                                                    )}
                                                    <Divider />
                                                </Col>
                                            </Row>

                                            <CustomFields
                                                customFields={accountType?.customFields}
                                                {...formikProps}
                                            />

                                            {/*{!recaptchaToken && (*/}
                                            {/*    <Row>*/}
                                            {/*        <Col xs={12}>*/}
                                            {/*            <GoogleReCaptchaProvider reCaptchaKey="6LcfAsgUAAAAAMhd7IC1gTPaRMlmOgn_zcAXpmma">*/}
                                            {/*                <GoogleReCaptcha*/}
                                            {/*                    onVerify={handleRecaptchaVerify}*/}
                                            {/*                />*/}
                                            {/*            </GoogleReCaptchaProvider>*/}
                                            {/*        </Col>*/}
                                            {/*    </Row>*/}
                                            {/*)}*/}
                                        </div>
                                    </Card.Content>
                                    <Card.Actions>
                                        <Card.Actions.Action
                                            text="Submit"
                                            onClick={handleSubmit}
                                            disabled={isSubmitting}
                                        />
                                    </Card.Actions>
                                </Card>

                                <Divider />
                                <p>
                                    Already have an account?{' '}
                                    <Link to="/login" state={{ from: location?.state?.from }}>
                                        Login
                                    </Link>{' '}
                                    now.
                                </p>

                                <small>
                                    This site is protected by reCAPTCHA and the Google{' '}
                                    <a href="https://policies.google.com/privacy" rel="noopener noreferrer">Privacy Policy</a>{' '}
                                    and{' '}
                                    <a href="https://policies.google.com/terms" rel="noopener noreferrer">Terms of Service</a>{' '}
                                    apply.
                                </small>
                            </Fragment>
                        );
                    }}
                </Formik>
            </Grid>
    );
};

export default RegisterForm;
